import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Award, Flame, Trophy, AlertCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ComposedChart, Line } from "recharts";

export default function Reports() {
  const [stores, setStores] = useState([]);
  const [directSales, setDirectSales] = useState(null);
  const [quarters, setQuarters] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    api.get("/reports/store-profits").then((r) => {
      setStores(r.data.stores || []);
      setDirectSales(r.data.direct_sales || null);
    });
    api.get("/reports/quarterly").then((r) => setQuarters(r.data));
    api.get("/reports/top-products").then((r) => setTopProducts(r.data));
    api.get("/reports/monthly").then((r) => setMonthly(r.data));
  }, []);

  const totalProfit = stores.reduce((s, x) => s + Number(x.profit || 0), 0);
  const totalRevenue = stores.reduce((s, x) => s + Number(x.received_value || 0), 0);
  const totalPotential = stores.reduce((s, x) => s + Number(x.potential_profit || 0), 0);

  return (
    <>
      <PageHeader
        title="Laporan Keuntungan"
        subtitle="Analisis laba per toko binaan & ringkasan kuartal (3 bulan)"
        testId="page-reports"
      />

      <Tabs defaultValue="monthly">
        <TabsList className="mb-4">
          <TabsTrigger value="monthly" data-testid="tab-monthly">Per Bulan</TabsTrigger>
          <TabsTrigger value="stores" data-testid="tab-stores">Per Toko Binaan</TabsTrigger>
          <TabsTrigger value="quarters" data-testid="tab-quarters">Per Kuartal (3 Bulan)</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produk Terlaris</TabsTrigger>
        </TabsList>

        {/* ---------- Per Bulan ---------- */}
        <TabsContent value="monthly">
          {monthly.length > 0 && (() => {
            const best = monthly.find((m) => m.is_best);
            const worst = monthly.find((m) => m.is_worst);
            const avgProfit = monthly.reduce((s, m) => s + m.net_profit, 0) / monthly.length;
            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Card className="p-5 border-2 border-emerald-400 bg-emerald-50/50 rounded-md stat-card">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-4 h-4 text-emerald-600"/>
                      <div className="overline">Bulan Terbaik</div>
                    </div>
                    <div className="font-bold text-lg" style={{fontFamily:'Outfit'}}>{best?.label}</div>
                    <div className="font-mono-num text-2xl font-bold text-[var(--hotto-success)] mt-1">{formatIDR(best?.net_profit || 0)}</div>
                  </Card>
                  <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
                    <div className="overline mb-2">Rata-rata Laba/Bulan</div>
                    <div className={`font-mono-num text-2xl font-bold ${avgProfit>=0?"text-[var(--hotto-primary)]":"text-red-600"}`}>{formatIDR(avgProfit)}</div>
                    <div className="text-xs text-slate-500 mt-1">{monthly.length} bulan tercatat</div>
                  </Card>
                  <Card className="p-5 border-2 border-red-300 bg-red-50/50 rounded-md stat-card">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-red-600"/>
                      <div className="overline">Bulan Terkurang</div>
                    </div>
                    <div className="font-bold text-lg" style={{fontFamily:'Outfit'}}>{worst?.label}</div>
                    <div className={`font-mono-num text-2xl font-bold mt-1 ${worst?.net_profit>=0?"text-slate-700":"text-red-600"}`}>{formatIDR(worst?.net_profit || 0)}</div>
                  </Card>
                </div>

                <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-4">
                  <div className="overline mb-3">Grafik Perbandingan Laba Bulanan</div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthly.map(m=>({name:m.label,"Laba Bersih":m.net_profit,"Pendapatan":m.revenue}))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                        <XAxis dataKey="name" stroke="#64748b" fontSize={11}/>
                        <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v)=>`${(v/1000).toFixed(0)}k`}/>
                        <Tooltip formatter={(v)=>formatIDR(v)}/>
                        <Legend/>
                        <Bar dataKey="Pendapatan" fill="#94a3b8" radius={[4,4,0,0]}/>
                        <Bar dataKey="Laba Bersih" fill="#047857" radius={[4,4,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </>
            );
          })()}

          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Bulan</TableHead>
                  <TableHead className="text-right">Pendapatan</TableHead>
                  <TableHead className="text-right">HPP</TableHead>
                  <TableHead className="text-right">Laba Kotor</TableHead>
                  <TableHead className="text-right">Beban</TableHead>
                  <TableHead className="text-right">Laba Bersih</TableHead>
                  <TableHead className="text-right">vs Bulan Lalu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthly.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada transaksi.</TableCell></TableRow>}
                {monthly.map((m, i) => {
                  const prev = i > 0 ? monthly[i-1].net_profit : null;
                  const delta = prev !== null ? m.net_profit - prev : null;
                  const pct = prev && prev !== 0 ? ((m.net_profit - prev) / Math.abs(prev)) * 100 : null;
                  return (
                    <TableRow key={m.key} className={`table-row-hover ${m.is_best?"bg-emerald-50/40":m.is_worst?"bg-red-50/40":""}`} data-testid={`monthly-${m.key}`}>
                      <TableCell className="font-semibold" style={{fontFamily:'Outfit'}}>
                        <div className="flex items-center gap-2">
                          {m.label}
                          {m.is_best && <Trophy className="w-3.5 h-3.5 text-emerald-600"/>}
                          {m.is_worst && !m.is_best && <AlertCircle className="w-3.5 h-3.5 text-red-500"/>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-slate-600">{formatIDR(m.revenue)}</TableCell>
                      <TableCell className="text-right font-mono-num text-slate-500">{formatIDR(m.cogs)}</TableCell>
                      <TableCell className="text-right font-mono-num">{formatIDR(m.gross_profit)}</TableCell>
                      <TableCell className="text-right font-mono-num text-[var(--hotto-destructive)]">{formatIDR(m.expense_total)}</TableCell>
                      <TableCell className="text-right font-mono-num font-bold">
                        <Badge className={m.net_profit>=0?"bg-emerald-50 text-emerald-700 hover:bg-emerald-50":"bg-red-50 text-red-700 hover:bg-red-50"}>
                          {formatIDR(m.net_profit)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-xs">
                        {delta === null ? <span className="text-slate-400">-</span> : (
                          <span className={delta>=0?"text-emerald-600":"text-red-600"}>
                            {delta>=0?"↑":"↓"} {formatIDR(Math.abs(delta))}
                            {pct !== null && <span className="ml-1 opacity-70">({pct>0?"+":""}{pct.toFixed(0)}%)</span>}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
          <div className="text-xs text-slate-500 mt-3">
            Baris hijau = bulan terbaik · Baris merah = bulan terkurang · % dibanding bulan sebelumnya.
          </div>
        </TabsContent>

        {/* ---------- Per Toko ---------- */}
        <TabsContent value="stores">
          {directSales && (
            <Card className="p-5 border-2 border-emerald-400 bg-emerald-50/40 rounded-md mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-emerald-600"/>Penjualan Langsung ke Konsumen</div>
                  <div className="text-xs text-slate-500">{directSales.sales_count} transaksi · {directSales.qty_sold} unit terjual</div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-right">
                  <div>
                    <div className="text-xs text-slate-500">Omzet</div>
                    <div className="font-mono-num font-semibold text-[var(--hotto-success)]">{formatIDR(directSales.revenue)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">HPP</div>
                    <div className="font-mono-num text-slate-500">{formatIDR(directSales.cogs)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Laba Bersih</div>
                    <div className={`font-mono-num font-bold ${directSales.profit>=0?"text-[var(--hotto-primary)]":"text-red-600"}`}>{formatIDR(directSales.profit)}</div>
                  </div>
                </div>
              </div>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
              <div className="overline mb-2">Total Diterima dari Toko</div>
              <div className="font-mono-num text-2xl font-bold text-[var(--hotto-success)]">{formatIDR(totalRevenue)}</div>
            </Card>
            <Card className="p-5 border-2 border-[var(--hotto-primary)] rounded-md stat-card">
              <div className="overline mb-2">Total Laba Bersih (Realized)</div>
              <div className={`font-mono-num text-2xl font-bold ${totalProfit>=0?"text-[var(--hotto-primary)]":"text-[var(--hotto-destructive)]"}`}>{formatIDR(totalProfit)}</div>
            </Card>
            <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
              <div className="overline mb-2">Potensi Laba (Stok Titipan)</div>
              <div className="font-mono-num text-2xl font-bold text-slate-700">{formatIDR(totalPotential)}</div>
            </Card>
          </div>

          {stores.length > 0 && (
            <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-4">
              <div className="overline mb-3">Perbandingan Laba Per Toko</div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stores.map(s=>({name:s.store_name, Laba:s.profit, "Potensi":s.potential_profit}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                    <XAxis dataKey="name" stroke="#64748b" fontSize={11}/>
                    <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v)=>`${(v/1000).toFixed(0)}k`}/>
                    <Tooltip formatter={(v)=>formatIDR(v)}/>
                    <Legend/>
                    <Bar dataKey="Laba" fill="#047857" radius={[4,4,0,0]}/>
                    <Bar dataKey="Potensi" fill="#94a3b8" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Toko</TableHead>
                  <TableHead className="text-right">Disalurkan</TableHead>
                  <TableHead className="text-right">Diterima</TableHead>
                  <TableHead className="text-right">HPP</TableHead>
                  <TableHead className="text-right">Laba Bersih</TableHead>
                  <TableHead className="text-right">Sisa Titipan</TableHead>
                  <TableHead className="text-right">Potensi Laba</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada data toko.</TableCell></TableRow>}
                {stores.map((s, i) => (
                  <TableRow key={s.store_id} className="table-row-hover" data-testid={`store-profit-${s.store_id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {i === 0 && s.profit > 0 && <Award className="w-4 h-4 text-amber-500"/>}
                        {s.store_name}
                      </div>
                      <div className="text-xs text-slate-500">{s.sales_count} laporan · {s.qty_sold} unit terjual</div>
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">{formatIDR(s.distributed_value)}</TableCell>
                    <TableCell className="text-right font-mono-num text-[var(--hotto-success)]">{formatIDR(s.received_value)}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-500">{formatIDR(s.cogs_sold)}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">
                      <Badge className={s.profit>=0?"bg-emerald-50 text-emerald-700 hover:bg-emerald-50":"bg-red-50 text-red-700 hover:bg-red-50"}>
                        {s.profit>=0?<TrendingUp className="w-3 h-3 mr-1"/>:<TrendingDown className="w-3 h-3 mr-1"/>}
                        {formatIDR(s.profit)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">
                      {s.remaining_stock_qty} unit · {formatIDR(s.remaining_stock_value)}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-slate-700">{formatIDR(s.potential_profit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div className="text-xs text-slate-500 mt-3">
            <b>Laba Bersih</b> = uang diterima dari toko - harga beli barang yang sudah terjual. <b>Potensi Laba</b> dihitung dari sisa stok titipan × selisih harga.
          </div>
        </TabsContent>

        {/* ---------- Per Kuartal ---------- */}
        <TabsContent value="quarters">
          {quarters.length > 0 && (
            <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-4">
              <div className="overline mb-3">Tren Kuartal</div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={quarters.map(q=>({name:q.label,Pendapatan:q.revenue,HPP:q.cogs,Beban:q.expense_total,"Laba Bersih":q.net_profit}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                    <XAxis dataKey="name" stroke="#64748b" fontSize={11}/>
                    <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v)=>`${(v/1000).toFixed(0)}k`}/>
                    <Tooltip formatter={(v)=>formatIDR(v)}/>
                    <Legend/>
                    <Bar dataKey="Pendapatan" fill="#16A34A" radius={[4,4,0,0]}/>
                    <Bar dataKey="HPP" fill="#f59e0b" radius={[4,4,0,0]}/>
                    <Bar dataKey="Beban" fill="#dc2626" radius={[4,4,0,0]}/>
                    <Bar dataKey="Laba Bersih" fill="#047857" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Kuartal</TableHead>
                  <TableHead className="text-right">Penjualan Langsung</TableHead>
                  <TableHead className="text-right">Pembayaran Toko</TableHead>
                  <TableHead className="text-right">Total Pendapatan</TableHead>
                  <TableHead className="text-right">HPP</TableHead>
                  <TableHead className="text-right">Laba Kotor</TableHead>
                  <TableHead className="text-right">Beban</TableHead>
                  <TableHead className="text-right">Laba Bersih</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quarters.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Belum ada data transaksi.</TableCell></TableRow>}
                {quarters.map((q) => (
                  <TableRow key={q.key} className="table-row-hover" data-testid={`quarter-${q.key}`}>
                    <TableCell className="font-semibold" style={{fontFamily:'Outfit'}}>{q.label}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">{formatIDR(q.revenue_direct)}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">{formatIDR(q.revenue_store)}</TableCell>
                    <TableCell className="text-right font-mono-num text-[var(--hotto-success)] font-semibold">{formatIDR(q.revenue)}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-500">{formatIDR(q.cogs)}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(q.gross_profit)}</TableCell>
                    <TableCell className="text-right font-mono-num text-[var(--hotto-destructive)]">{formatIDR(q.expense_total)}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">
                      <Badge className={q.net_profit>=0?"bg-emerald-50 text-emerald-700 hover:bg-emerald-50":"bg-red-50 text-red-700 hover:bg-red-50"}>
                        {formatIDR(q.net_profit)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div className="text-xs text-slate-500 mt-3">
            Kuartal: Q1 (Jan-Mar) · Q2 (Apr-Jun) · Q3 (Jul-Sep) · Q4 (Okt-Des). Laba Bersih = Pendapatan - HPP - Beban.
          </div>
        </TabsContent>

        {/* ---------- Produk Terlaris (Pareto) ---------- */}
        <TabsContent value="products">
          {topProducts.length > 0 && (
            <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-4 h-4 text-orange-500"/>
                <div className="overline">Pareto Produk Terlaris</div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={topProducts.slice(0,15).map(p=>({name:p.name.slice(0,15), "Qty Terjual":p.qty_sold, "% Kumulatif":p.cumulative_pct}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                    <XAxis dataKey="name" stroke="#64748b" fontSize={10} angle={-25} textAnchor="end" height={60}/>
                    <YAxis yAxisId="left" stroke="#64748b" fontSize={11}/>
                    <YAxis yAxisId="right" orientation="right" stroke="#EA580C" fontSize={11} domain={[0,100]} tickFormatter={(v)=>`${v}%`}/>
                    <Tooltip/>
                    <Legend/>
                    <Bar yAxisId="left" dataKey="Qty Terjual" fill="#047857" radius={[4,4,0,0]}/>
                    <Line yAxisId="right" type="monotone" dataKey="% Kumulatif" stroke="#EA580C" strokeWidth={2} dot={{r:3}}/>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Produk</TableHead>
                  <TableHead className="text-right">Qty Terjual</TableHead>
                  <TableHead className="text-right">Omzet</TableHead>
                  <TableHead className="text-right">Laba</TableHead>
                  <TableHead className="text-right">Stok Gudang</TableHead>
                  <TableHead className="text-right">% Kumulatif</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topProducts.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada penjualan.</TableCell></TableRow>}
                {topProducts.map((p, i) => (
                  <TableRow key={p.product_id} className="table-row-hover" data-testid={`top-product-${p.product_id}`}>
                    <TableCell className="font-mono-num text-slate-500">
                      {i < 3 ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">#{i+1}</Badge> : `#${i+1}`}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.sku || "-"}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{p.qty_sold} {p.unit}</TableCell>
                    <TableCell className="text-right font-mono-num text-[var(--hotto-success)]">{formatIDR(p.revenue)}</TableCell>
                    <TableCell className="text-right font-mono-num text-[var(--hotto-primary)]">{formatIDR(p.profit)}</TableCell>
                    <TableCell className="text-right font-mono-num">
                      {p.current_stock <= 5 ? (
                        <Badge variant="outline" className="border-[var(--hotto-alert)] text-[var(--hotto-alert)]">{p.current_stock}</Badge>
                      ) : p.current_stock}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">{p.cumulative_pct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div className="text-xs text-slate-500 mt-3">
            Analisa Pareto 80/20: fokuskan restock pada produk yang menyumbang 80% penjualan (baris paling atas).
          </div>
        </TabsContent>
      </Tabs>
    </>
  );
}
